import { Box, Typography, Button, styled, alpha, Tab, Tabs } from '@mui/material';

const primaryColor = '#5E8E7E';

export const PageWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
}));

export const DetailCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e0e0e0',
  overflow: 'hidden',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
}));

export const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  borderBottom: '1px solid #f0f0f0',
}));

export const PlanInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const PlanIconBox = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '12px',
  backgroundColor: '#E8F0FE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#1967D2',
});

export const StatusBadge = styled(Box)<{ active: boolean }>(({ active }) => ({
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: active ? alpha('#2E7D32', 0.1) : alpha('#d32f2f', 0.1),
  color: active ? '#2E7D32' : '#d32f2f',
  width: 'fit-content',
  marginTop: '4px',
}));

export const HeaderActions = styled(Box)({
  display: 'flex',
  gap: '12px',
});

export const DeleteButton = styled(Button)({
  textTransform: 'none',
  borderColor: '#d32f2f',
  color: '#d32f2f',
  padding: '6px 24px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: alpha('#d32f2f', 0.05),
    borderColor: '#d32f2f',
  }
});

export const EditButton = styled(Button)({
  textTransform: 'none',
  backgroundColor: primaryColor,
  color: '#fff',
  padding: '6px 32px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: alpha(primaryColor, 0.9),
  }
});

export const StyledTabs = styled(Tabs)({
  padding: '0 24px',
  borderBottom: '1px solid #f0f0f0',
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
});

export const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  color: '#666',
  '&.Mui-selected': {
    color: primaryColor,
  },
});

export const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  backgroundColor: primaryColor,
  color: '#fff',
  padding: theme.spacing(1.2, 2),
  fontSize: '0.95rem',
  fontWeight: 600,
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
}));

export const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: theme.spacing(3),
  backgroundColor: '#fff',
}));

export const DataItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const DataLabel = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
  fontWeight: 500,
});

export const DataValue = styled(Typography)({
  fontSize: '1rem',
  color: '#1A1C1E',
  fontWeight: 600,
});

export const FeatureGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  padding: '24px',
});

export const FeatureBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  backgroundColor: '#F5F7F6',
  borderRadius: '8px',
  color: '#1A1C1E',
  fontSize: '0.875rem',
  fontWeight: 500,
  border: '1px solid #E0E0E0',
  '& svg': {
    color: primaryColor,
    fontSize: '18px',
  }
});

export const OrgTableWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: '0',
  },
  '& .MuiTableCell-head': {
    backgroundColor: primaryColor,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
  },
  '& .MuiTableCell-body': {
    padding: theme.spacing(2),
    fontSize: '0.875rem',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: '#FAFAFA',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(primaryColor, 0.02),
  }
}));

export const ProgressBar = styled(Box)<{ percentage: number; color?: string }>(({ percentage, color }) => ({
  width: '100%',
  height: '6px',
  backgroundColor: '#E0E0E0',
  borderRadius: '3px',
  marginTop: '8px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${Math.min(percentage, 100)}%`,
    backgroundColor: color || primaryColor,
    borderRadius: '3px',
  }
}));

export const SubStatusBadge = styled(Box)<{ status: string }>(({ status }) => ({
  padding: '4px 12px',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 600,
  textAlign: 'center',
  width: 'fit-content',
  backgroundColor: status === 'Active' ? alpha('#2E7D32', 0.1) : alpha('#FFB300', 0.1),
  color: status === 'Active' ? '#2E7D32' : '#FFB300',
}));
